<template>
  <div class="card d-flex flex-column w-100">
    <article
      v-if="!nested && (title || description || $slots['header-actions'])"
      class="card-header mx-0 px-3 border-0 d-flex justify-content-between flex-shrink-0"
    >
      <header v-if="title">
        <h4 class="mb-1" style="text-transform: capitalize">{{ title }}</h4>
        <p v-if="description" class="mb-0">{{ description }}</p>
      </header>
      <slot name="header-actions"></slot>
    </article>
    <DataTableToolbar
      v-if="selectable || searchable || $slots['toolbar-actions'] || paginated"
      class="z-3"
      :selected="selectedIds"
      :searchable="searchable"
      :search="props.search"
      :createable="createable"
      :deletable="deletable && allIds.length > 0"
      :updatable="updatable"
      :refreshable="paginated"
      :loading="loading"
      :error-message="errorMessage"
      :nested="nested"
      :class="[
        {'z-1': selectedIds.length === 0 && selectable},
        {'mt-3': !(title || description || nested || $slots['header-actions'])},
      ]"
      @deselect="handleSelectAllModel = false"
      @create="$emit('create')"
      @update="$emit('update', selectedIds[0])"
      @delete="$emit('delete', selectedIds)"
      @refresh="updateCurrentPage(currentPage)"
      @search="updateSearch"
    >
      <template v-if="$slots['toolbar-actions-selected']" #toolbar-actions-selected>
        <slot name="toolbar-actions-selected" :selected-ids="selectedIds" />
      </template>
      <template v-if="$slots['toolbar-actions']" #toolbar-actions>
        <slot name="toolbar-actions"></slot>
      </template>
      <template v-if="$slots['toolbar-actions-left']" #toolbar-actions-left>
        <slot name="toolbar-actions-left" />
      </template>
      <template v-if="$slots['extra-toolbar-row']" #extra-toolbar-row>
        <slot name="extra-toolbar-row" :selected-ids="selectedIds" />
      </template>
    </DataTableToolbar>

    <div
      :class="[
        'd-block overflow-auto custom-scrollbar flex-grow-1',
        {'rounded-bottom': !paginated},
        {'z-1': selectedIds.length > 0},
      ]"
    >
      <table class="table table-hover table-striped mb-0">
        <DataTableHeader
          v-model:selected="handleSelectAllModel"
          v-model:expanded="handleExpandAllModel"
          class="sticky-header"
          :selectable="selectable"
          :expandable="expandable"
          :columns="colsKeys"
          :default-columns="defaulCols"
          :sortable="sortable && rows.length > 0"
          :sort-direction="sortDirection"
          :sort-key="sortKey"
          :updatable-inline="updatableInline || Boolean($slots.actions)"
          :deletable-inline="deletableInline || Boolean($slots.actions)"
          :disabled="loading || loadingCol !== undefined || loadingRow !== undefined"
          :tooltips="columnTooltips"
          @update:sort-direction="handleSortDirection"
        />
        <tbody>
          <!-- Loading rows -->
          <DataTableLoading
            v-if="props.loading === true"
            :columns="colsKeys"
            :default-columns="defaulCols"
            :selectable="selectable"
            :expandable="expandable"
            :actionable="$slots.actions !== undefined"
            :rows="placeholderRows"
          />

          <template v-if="props.loading === false && sortedRows.length > 0 && colsKeys.length > 0">
            <template v-for="(row, rowIndex) in sortedRows" :key="rowIndex">
              <!-- Regular row -->
              <DataTableRow
                v-model:selected="selectedIds"
                v-model:expanded="expandedIds"
                :expandable="expandable"
                :nested="row[nestedKey]?.length > 0"
                :selectable="selectable"
                :value="String(row?.id)"
                :disabled="loading || loadingCol !== undefined || loadingRow !== undefined"
                :class="{
                  'last-nested-row': (nested || !paginated) && rowIndex + 1 === sortedRows.length,
                }"
                :clickable="clickable"
                @click-inline="$emit('clickInline', row)"
              >
                <slot v-if="$slots.customCells" name="customCells" :row="row" :cols="colsValues" />
                <template v-for="(col, _colIndex) in colsValues" v-else :key="_colIndex">
                  <slot
                    v-if="$slots.customCell && col.startsWith('#')"
                    :key="col"
                    name="customCell"
                    :row="row"
                    :col="col"
                  />
                  <DataTableCell
                    v-else
                    :row="row"
                    :col="col"
                    :cols-values="colsValues"
                    :loading-col="loadingCol"
                    :loading-row="loadingRow"
                    :cell-checks="cellChecks"
                    @update="updateValue"
                  >
                  </DataTableCell>
                </template>
                <DataTableRowActions
                  v-if="updatableInline || deletableInline || $slots.actions"
                  :row="row"
                  :loading-col="loadingCol"
                  :loading-row="loadingRow"
                  :updatable-inline="updatableInline"
                  :deletable-inline="deletableInline"
                  @update-inline="$emit('updateInline', row)"
                  @delete-inline="$emit('deleteInline', row)"
                >
                  <template v-if="$slots.actions" #extraActions="slotProps">
                    <slot name="actions" :row="row" :rowid="slotProps.rowid" />
                  </template>
                </DataTableRowActions>
              </DataTableRow>
              <!-- Expanded row -->
              <template v-if="expandedIds.includes(String(row?.id)) && row[nestedKey]?.length > 0">
                <DataTableRow class="expanded-tr">
                  <!-- Match action cols -->
                  <td
                    v-if="expandable || selectable"
                    :colspan="expandable && selectable ? 2 : 1"
                  ></td>
                  <td colspan="999" class="py-0 px-0">
                    <!-- Table wrapper should determine the type of nested rows -->
                    <slot
                      name="nestedRow"
                      :nested-rows="row[nestedKey]"
                      :nested-cols-map="nestedColsMap"
                    />
                  </td>
                </DataTableRow>
              </template>
            </template>
          </template>

          <!-- Empty row -->
          <tr v-else-if="props.loading === false">
            <td colspan="999">
              <span class="d-flex justify-content-center">{{ translate('No data found') }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <DataTablePagination
      v-if="paginated && !nested"
      :count="count"
      :page="currentPage"
      :total="total"
      :limit="limit"
      class="table-footer px-3 z-2 flex-shrink-0"
      @update:page="(value: number) => updateCurrentPage(value)"
      @update:page-limit="(value: number) => updatePageLimit(value)"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends {[key: string]: any}">
import {useLocalisation} from '@/composables/useLocalisation';

const {translate} = useLocalisation();

const props = withDefaults(
  defineProps<{
    rows?: T[];
    limit?: number;
    offset?: number;
    page?: number;
    title?: string;
    description?: string;
    total?: number;
    loadingCol?: string;
    loadingRow?: string;
    loading?: boolean;
    colsMap?: {[key: string]: string};
    columnTooltips?: {[label: string]: string};
    nestedColsMap?: {[key: string]: string};
    sortable?: boolean;
    expandable?: boolean;
    nestedKey?: string;
    selectable?: boolean;
    clickable?: boolean;
    searchable?: boolean;
    search?: string;
    createable?: boolean;
    updatable?: boolean;
    updatableInline?: boolean;
    deletable?: boolean;
    deletableInline?: boolean;
    errorMessage?: string;
    paginated?: boolean;
    nested?: boolean; // Is the table nested in another?
    cellChecks?: boolean; // Enable inline checkboxes?
    placeholderRows?: number; // How many placeholder rows to show while loading
  }>(),
  {
    rows: () => [],
    limit: 0,
    offset: 0,
    page: 0,
    title: '',
    description: undefined,
    total: 0,
    loadingCol: undefined,
    loadingRow: undefined,
    loading: false,
    colsMap: undefined,
    columnTooltips: undefined,
    nestedColsMap: () => ({}),
    sortable: true,
    selectable: false,
    clickable: false,
    search: '',
    nestedKey: '',
    expandable: false,
    paginated: false,
    nested: false,
    errorMessage: '',
    cellChecks: false,
    placeholderRows: undefined,
  },
);

const emits = defineEmits<{
  create: [];
  update: [value: string];
  updateInline: [value: T];
  delete: [value: string[]];
  deleteInline: [value: T];
  clickInline: [value: T];
  search: [search: string];
  'update:page': [value: number];
  'update:page-limit': [value: number];
  rowDelete: [item: any];
  rowUpdate: [item: any];
  cellCreate: [colKey: string | number, row: any];
  cellDelete: [colKey: string | number, row: any];
  'update:sort': [sort: {column: string; direction: 1 | -1} | null];
}>();

// Different results may have a myriad of different item types.
const mapKeysToCols = (item: {[key: string]: string}) => {
  const _colsMap: {[key: string]: string} = {};
  for (const k of Object.keys(item)) {
    const capitalised = k.charAt(0).toUpperCase() + k.slice(1);
    _colsMap[capitalised] = k;
  }
  return _colsMap;
};

const defaulCols = Array.apply(null, Array(5)).map((_) => '');
const colsMap = computed(() => {
  if (props.colsMap) return props.colsMap;
  else return props.rows.length > 0 ? mapKeysToCols(props.rows[0]) : {};
});
const colsKeys = computed<string[]>(() => Object.keys(colsMap.value) || []);
const colsValues = computed<string[]>(() => Object.values(colsMap.value) || []);

/**
 * Selectable
 */

// NB: Do not try to select all ids in nested rows, nested tables should be isolated
const allIds = computed(() => props.rows.map((r) => String(r?.id)));

const selectedIds = ref<string[]>([]);

const handleSelectAllModel = computed({
  get: () => (allIds.value ? selectedIds.value.length === allIds.value.length : false),
  set: (value) => {
    const selected: string[] = [];
    if (value) {
      allIds.value.forEach(function (id) {
        selected.push(id);
      });
    }
    selectedIds.value = selected;
  },
});

/**
 * Expandable
 */

const expandedIds = ref<string[]>([]);
const handleExpandAllModel = computed({
  get: () => (allIds.value ? expandedIds.value.length === allIds.value.length : false),
  set: (value) => {
    const expanded: string[] = [];
    if (value) {
      allIds.value.forEach(function (id) {
        expanded.push(id);
      });
    }
    expandedIds.value = expanded;
  },
});

const sortKey = ref<string>();
const sortDirection = ref(1);

/**
 *
 */
const sortedRows = computed(() => {
  return sortKey.value !== undefined
    ? props.rows.slice().sort((a, b) => {
        const sortValue = sortKey.value !== undefined ? colsMap.value[sortKey.value] : '';
        let valueA = a[sortValue];
        let valueB = b[sortValue];

        return typeof valueA === 'string'
          ? valueA.localeCompare(valueB) * sortDirection.value
          : (valueA - valueB) * sortDirection.value;
      })
    : props.rows;
});

/**
 *
 */
const handleSortDirection = (column: string) => {
  if (sortKey.value === column && sortDirection.value === -1) {
    sortKey.value = undefined;
    emits('update:sort', null);
  } else if (sortKey.value === column) {
    sortDirection.value = -1;
    emits('update:sort', {column, direction: -1});
  } else {
    sortKey.value = column;
    sortDirection.value = 1;
    emits('update:sort', {column, direction: 1});
  }
};

/**
 * Pagination
 */
const currentPage = ref(1);
const count = computed(() => Math.ceil(props.total / props.limit));
const currentOffset = computed(() => (currentPage.value - 1) * props.limit || 0);
const updateCurrentPage = (page: number) => {
  currentPage.value = page;
  emits('update:page', currentOffset.value);
};
const updateSearch = (search: string) => {
  emits('search', search);
  currentPage.value = 0;
};

/**
 * Format for POSTing or DELETing permissions
 * {@link api/permissions/group/<int:group_pk>/core/<int:core_pk>/}
 * {@link api/permissions/group/<int:group_pk>/user/<int:user_pk>/}
 * {@link api/permissions/group/<int:group_pk>/navigation/<int:user_pk>/}
 */
const updateValue = (colKey: string | number, row: any, value: boolean) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  value ? emits('cellCreate', colKey, row) : emits('cellDelete', colKey, row);
};

watch(
  () => props.loading,
  () => (selectedIds.value = []),
);

const updatePageLimit = (value: number) => {
  currentPage.value = 0; // Follow the page limit reset
  emits('update:page-limit', value);
};
</script>
<style lang="scss" scoped>
.card {
  max-height: 100%;

  --bs-card-cap-padding-y: 1rem;
  --bs-card-border-width: 0 !important;
}

.table-footer {
  position: relative;
  z-index: 1;
  display: flex;
  inline-size: 100%;
  min-block-size: 3rem;
}

.table {
  --bs-table-striped-bg: color-mix(in srgb, var(--bs-blue-500) 7.5%, transparent);
}

.table :deep(.checkbox-expanded),
.table :deep(.checkbox-selected) {
  width: 2rem;
}

.last-nested-row :deep(td) {
  border-bottom: 0;
}

.table :deep(td) {
  vertical-align: middle;
}

// Disable hover nested rows, overwrite using body colours
.expanded-tr:hover > *,
.expanded-tr > * {
  --bs-table-color-state: var(--bs-body-color);
  --bs-table-bg-state: var(--bs-body-bg);
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: var(--bs-body-bg);
}
</style>
